import { Injectable } from "@angular/core";
import { HttpHeaders, HttpParams, HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../environments/environment";
import { HeaderInterface, ParamsInterface } from "../models/http-header.model";
import { finalize, tap } from "rxjs/operators";
import { LoaderAppService } from "src/pages/shared/loader/loader.service";

@Injectable({
  providedIn: "root",
})
export class HttpService {
  protected _baseUrl: string = environment.base_url;

  constructor(
    protected _httpClient: HttpClient,
    protected _loaderService: LoaderAppService
  ) {}

  /**
   * Function to Add Custom Headers and return it
   * @param headerArr Array<Object>
   * @return headers Object
   */
  setAdditionalHeaders(headerArr?: Array<HeaderInterface>): HttpHeaders {
    let headers = new HttpHeaders();
    if (headerArr) {
      headerArr.forEach((headerObj) => {
        let key = headerObj["key"];
        let value = headerObj["value"];
        headers = headers.set(key, value);
      });
    }
    return headers;
  }

  /**
   * Function to Add Custom Headers and return it
   * @param paramsArr Array<Object>
   * @return params
   */
  generateParams(paramsArr: ParamsInterface[]): HttpParams {
    let params = new HttpParams();
    paramsArr.forEach((paramObj) => {
      let key = paramObj["key"];
      let value = paramObj["value"];
      let param: any = {};
      param[key] = value;
      params = params.appendAll(param);
    });
    return params;
  }

  /**
   * Method to call get request api
   */
  getRequest(
    url: string,
    paramsArr?: ParamsInterface[],
    headerArr?: Array<HeaderInterface>,
    loader = true,
    isFile?: boolean,
    apiPrefix?: string
  ): Observable<any> {
    const headers = this.setAdditionalHeaders(headerArr);
    let params = new HttpParams();

    if (paramsArr) {
      paramsArr.forEach((paramObj) => {
        let key = paramObj["key"];
        let value = paramObj["value"];
        params = params.set(key, value);
      });
    }

    let httpOptions: any = {
      headers: headers,
      params: params,
    };

    if (isFile) {
      if (url.includes("promotional-emails")) {
        httpOptions["responseType"] = "blob" as "json";
      } else {
        httpOptions["responseType"] = "blob";
      }
    }
    if (loader) {
      this._loaderService.showLoader();
    }
    return this._httpClient
      .get(`${apiPrefix || this._baseUrl}/${url}`, httpOptions)
      .pipe(
        finalize(() => {
          if (loader) this._loaderService?.hideLoader();
        })
      );
  }

  getDeptRequest(
    url: string,
    paramsArr?: ParamsInterface[],
    headerArr?: Array<HeaderInterface>,
    loader = true,
    isFile?: boolean
  ): Observable<any> {
    const headers = this.setAdditionalHeaders(headerArr);
    let params = new HttpParams();
    if (paramsArr) {
      params = this.generateParams(paramsArr);
    }

    let httpOptions: any = {
      headers: headers,
      params: params,
    };

    if (isFile) {
      httpOptions["responseType"] = "blob";
    }
    if (loader) {
      this._loaderService.showLoader();
    }

    return this._httpClient.get(`${url}`, httpOptions).pipe(
      tap({
        next: (event) => {
          if (loader) this._loaderService?.hideLoader();
        },
        error: (err) => {
          if (loader) this._loaderService?.hideLoader();
        },
      })
    );
  }

  /**
   * Method to call the post request api
   */
  postRequest(
    url: string,
    body?: any,
    params?: HttpParams,
    headerArr?: Array<HeaderInterface>,
    loader = true,
    isFile?: boolean,
    setResponseText?: boolean,
    apiPrefix?: string
  ): Observable<any> {
    const headers = this.setAdditionalHeaders(headerArr);
    let httpOptions: any = {
      headers: headers,
      params: params!,
    };
    if (setResponseText) {
      let response = { observe: "response", responseType: "text" };
      httpOptions = { ...httpOptions, ...response };
    }
    if (loader) {
      this._loaderService.showLoader(url);
    }
    return this._httpClient
      .post(`${this._baseUrl}/${url}`, body, httpOptions)
      .pipe(
        tap({
          next: (event) => {
            if (loader) this._loaderService?.hideLoader();
          },
          error: (err) => {
            if (loader) this._loaderService?.hideLoader();
          },
          finalize: () => {
            if (loader) this._loaderService?.hideLoader();
          },
        })
      );
  }

  /**
   * Method to call get request api
   */
  deleteRequest(
    url: string,
    body?: any,
    params?: HttpParams,
    headerArr?: Array<HeaderInterface>,
    loader = true,
    apiPrefix?: string
  ): Observable<any> {
    const headers = this.setAdditionalHeaders(headerArr);
    let httpOptions: any = {
      headers: headers,
      params: params!,
      body,
    };
    if (loader) {
      this._loaderService.showLoader();
    }
    return this._httpClient.delete(`${this._baseUrl}/${url}`, httpOptions).pipe(
      tap({
        next: (event) => {
          if (loader) this._loaderService?.hideLoader();
        },
        error: (err) => {
          if (loader) this._loaderService?.hideLoader();
        },
      })
    );
  }

  /**
   * Method to call the put request api
   */
  putRequest(
    url: string,
    body?: Object,
    params?: HttpParams,
    headerArr?: Array<HeaderInterface>,
    loader = true,
    apiPrefix?: string
  ): Observable<any> {
    const headers = this.setAdditionalHeaders(headerArr);
    let httpOptions: any = {
      headers: headers,
      params: params!,
    };
    if (loader) {
      this._loaderService.showLoader();
    }
    return this._httpClient
      .put(`${this._baseUrl}/${url}`, body, httpOptions)
      .pipe(
        tap({
          next: (event) => {
            if (loader) this._loaderService?.hideLoader();
          },
          error: (err) => {
            if (loader) this._loaderService?.hideLoader();
          },
        })
      );
  }

  /**
   * Method to call the patch request api
   */
  patchRequest(
    url: string,
    body: Object,
    params?: HttpParams,
    headerArr?: Array<HeaderInterface>,
    loader = true,
    apiPrefix?: string
  ): Observable<any> {
    const headers = this.setAdditionalHeaders(headerArr);
    let httpOptions: any = {
      headers: headers,
      params: params!,
    };
    if (loader) {
      this._loaderService.showLoader();
    }
    return this._httpClient
      .patch(`${this._baseUrl}/${url}`, body, httpOptions)
      .pipe(
        tap({
          next: (event) => {
            if (loader) this._loaderService?.hideLoader();
          },
          error: (err) => {
            if (loader) this._loaderService?.hideLoader();
          },
        })
      );
  }
}
